import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const COMPANY_LOGO = "/images/assets/h0kydekd-logo-adjust.png";

// Hero Section
function HeroSection() {
  return (
    <section 
      className="min-h-screen flex items-center justify-center bg-white relative overflow-hidden"
      data-testid="hero-section"
    >
      {/* Subtle abstract light effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-gradient-to-br from-orange-50 to-transparent rounded-full blur-3xl opacity-60" />
        <div className="absolute bottom-1/3 left-1/3 w-64 h-64 bg-gradient-to-tr from-amber-50 to-transparent rounded-full blur-3xl opacity-40" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 text-center relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Main Catch Copy */}
          <h1 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6 animate-fade-in-up"
            data-testid="hero-title"
          >
            ひとつのマッチが、<br />
            <span className="text-[#FF4D00]">未来に火を灯す。</span>
          </h1>
          
          <p 
            className="font-en text-base md:text-lg text-gray-500 mb-12 animate-fade-in-up animation-delay-100"
            data-testid="hero-subtitle-en"
          >
            One Match Lights the Future.
          </p>

          {/* Statement */}
          <div className="space-y-6 mb-12 animate-fade-in-up animation-delay-200">
            <p className="text-base md:text-lg text-gray-700 leading-relaxed">
              挑戦者にWinを届けるために。
            </p>
            <p className="text-base md:text-lg text-gray-700 leading-relaxed">
              テクノロジーと感性で、<br className="md:hidden" />心が燃える事業を生み出す。
            </p>
            <p className="text-sm md:text-base text-gray-500 leading-relaxed">
              そのために私たちは、<br />
              相手ファースト×スピード×しくみで着火し、<br />
              ひとつの事業から、この国に大きな炎を灯し続ける。
            </p>
          </div>

          {/* Company Name */}
          <div className="animate-fade-in-up animation-delay-300">
            <img 
              src={COMPANY_LOGO} 
              alt="IT&Plucktice" 
              className="h-10 md:h-12 mx-auto"
              data-testid="hero-logo"
            />
          </div>
        </div>
      </div>
    </section>
  );
}

// Philosophy Section
function PhilosophySection() {
  return (
    <section 
      className="py-24 md:py-32 bg-white"
      data-testid="philosophy-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-12">
          {/* Purpose */}
          <div className="text-center">
            <p className="font-en text-xs font-semibold text-[#FF4D00] tracking-widest mb-3">PURPOSE</p>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900">
              ひとつのマッチが、<br />未来に火を灯す。
            </h3>
          </div>

          {/* Mission */}
          <div className="text-center">
            <p className="font-en text-xs font-semibold text-[#FF4D00] tracking-widest mb-3">MISSION</p>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900">
              挑戦者にWinを。
            </h3>
          </div>

          {/* Vision */}
          <div className="text-center">
            <p className="font-en text-xs font-semibold text-[#FF4D00] tracking-widest mb-3">VISION</p>
            <h3 className="text-xl md:text-2xl font-bold text-gray-900">
              テクノロジーと感性で、<br />心が燃える事業を。
            </h3>
          </div>
        </div>

        <div className="text-center">
          <Link
            to="/philosophy"
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[#FF4D00] transition-colors duration-300 group"
            data-testid="philosophy-link"
          >
            <span className="font-en">View Philosophy</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// Business Section
function BusinessSection() {
  const businesses = [
    {
      id: "talent",
      title: "Talent Matching",
      description: "専門性の高い人材と企業を結び、\n最適なマッチングを創出。",
    },
    {
      id: "career",
      title: "Career Matching",
      description: "企業と個人の持続的な成長を実現する\n人材紹介事業。",
    },
    {
      id: "growth",
      title: "Growth Matching",
      description: "企業が抱えるマーケティング課題に、\n適切なソリューションを提供。",
    },
  ];

  return (
    <section 
      className="py-24 md:py-32 bg-gray-50"
      data-testid="business-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16">
          <p className="font-en text-xs font-semibold text-[#FF4D00] tracking-widest mb-3">
            What We Match
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Our Business
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl leading-relaxed">
            私たちは、専門人材とテクノロジーを基盤に、<br className="hidden md:block" />
            挑戦する個人と企業にWinを届ける事業を構築しています。
          </p>
        </div>

        {/* Business Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 mb-12">
          {businesses.map((business) => (
            <div
              key={business.id}
              className="bg-white border border-gray-100 hover:border-gray-200 hover:shadow-xl hover:shadow-gray-200/50 transition-all duration-500 rounded-xl p-8"
              data-testid={`business-card-${business.id}`}
            >
              <h3 className="font-en text-lg font-bold text-gray-900 mb-3">
                {business.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">
                {business.description}
              </p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/business"
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[#FF4D00] transition-colors duration-300 group"
            data-testid="business-link"
          >
            <span className="font-en">View Our Businesses</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// Leadership Section
function LeadershipSection() {
  const leaders = [
    {
      id: "kawamata",
      name: "川俣 貴裕",
      nameEn: "Takahiro Kawamata",
      title: "代表取締役",
      belief: "お役立ちの総量を最大化する",
      photo: "/images/assets/leader-kawamata.webp",
    },
    {
      id: "watanabe",
      name: "渡邉 有哉",
      nameEn: "Yuya Watanabe",
      title: "取締役",
      belief: "為せば成る、為さねば成らぬ何事も、成らぬは人の為さぬなりけり",
      photo: "/images/assets/4kkrteuj-image-2026-2-16.png",
    },
  ];

  return (
    <section 
      className="py-24 md:py-32 bg-white"
      data-testid="leadership-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-16">
          <p className="font-en text-xs font-semibold text-[#FF4D00] tracking-widest mb-3">
            Top of Match
          </p>
          <h2 className="font-en text-3xl md:text-4xl font-bold text-gray-900">
            Leadership
          </h2>
        </div>

        {/* Leader Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 max-w-4xl mx-auto mb-12">
          {leaders.map((leader) => (
            <div
              key={leader.id}
              className="text-center"
              data-testid={`leader-card-${leader.id}`}
            >
              {/* Photo */}
              {leader.photo ? (
                <div className="w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden">
                  <img 
                    src={leader.photo} 
                    alt={leader.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="w-32 h-32 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                  <span className="text-3xl font-bold text-gray-300">
                    {leader.name.charAt(0)}
                  </span>
                </div>
              )}
              
              <p className="text-sm text-gray-500 mb-1">{leader.title}</p>
              <h3 className="text-xl font-bold text-gray-900 mb-1">{leader.name}</h3>
              <p className="font-en text-sm text-gray-500">{leader.nameEn}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <Link
            to="/leadership"
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-[#FF4D00] transition-colors duration-300 group"
            data-testid="leadership-link"
          >
            <span className="font-en">View Leadership</span>
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </div>
    </section>
  );
}

// Company Section
function CompanySection() {
  const companyInfo = [
    { label: "社名", value: "株式会社IT&Plucktice" },
    { label: "設立", value: "2019年4月" },
    { label: "資本金", value: "100万円" },
    { label: "所在地", value: "東京都渋谷区代々木１丁目３０−１５ 天翔代々木ビル" },
    { label: "代表", value: "川俣貴裕" },
    { label: "取引銀行", value: "城南信用金庫、三菱UFJ銀行、みずほ銀行" },
    { label: "事業内容", value: "フリーランスマッチング事業、人材紹介事業（13-ユ-315262）、新規事業開発" },
  ];

  return (
    <section 
      id="company"
      className="py-24 md:py-32 bg-gray-50"
      data-testid="company-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <p className="font-en text-xs font-semibold text-[#FF4D00] tracking-widest mb-3">
            Box of Match
          </p>
          <h2 className="font-en text-3xl md:text-4xl font-bold text-gray-900">
            Company
          </h2>
        </div>

        {/* Company Info Table */}
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <dl>
            {companyInfo.map((item, index) => (
              <div
                key={item.label}
                className={`grid grid-cols-1 md:grid-cols-4 ${
                  index !== companyInfo.length - 1 ? "border-b border-gray-100" : ""
                }`}
                data-testid={`company-info-${index}`}
              >
                <dt className="px-6 py-4 bg-gray-50 md:bg-transparent font-medium text-sm text-gray-700">
                  {item.label}
                </dt>
                <dd className="px-6 py-4 md:col-span-3 text-sm text-gray-600">
                  {item.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div className="pt-16 md:pt-20" data-testid="home-page">
      <HeroSection />
      <PhilosophySection />
      <BusinessSection />
      <LeadershipSection />
      <CompanySection />
    </div>
  );
}
